import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useContent } from "../contexts/ContentContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { useReservation } from "./ReservationDialog";

const navItems = (t) => [
    { id: "about", label: t?.nav?.about || "La casa" },
    { id: "menu", label: t?.nav?.menu || "Carta" },
    { id: "gallery", label: t?.nav?.gallery || "Galeria" },
    { id: "faq", label: t?.nav?.faq || "Preguntes" },
    { id: "contact", label: t?.nav?.contact || "Contacte" },
];

export default function Header() {
    const { t, lang, setLang } = useContent();
    const { setOpen: setReserveOpen } = useReservation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    const openReserve = () => {
        setOpen(false);
        setReserveOpen(true);
    };

    return (
        <>
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_1px_0_rgba(35,58,90,0.08)]" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
                <a
                    href="#top"
                    data-testid="brand-logo"
                    className="font-display text-3xl md:text-4xl font-medium tracking-tight"
                    style={{ color: scrolled ? "#233A5A" : "#fff", textShadow: scrolled ? "none" : "0 2px 12px rgba(0,0,0,.35)" }}
                >
                    Zarautz
                </a>

                <nav className="hidden lg:flex items-center gap-10">
                    {navItems(t).map((item) => (
                        <button
                            key={item.id}
                            onClick={() => go(item.id)}
                            data-testid={`nav-${item.id}`}
                            className={`text-sm font-medium tracking-wide transition-colors hover:text-brand-terracotta ${
                                scrolled ? "text-brand-navy" : "text-white drop-shadow"
                            }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3 md:gap-4">
                    <LanguageSwitcher lang={lang} setLang={setLang} />
                    <button
                        type="button"
                        onClick={openReserve}
                        data-testid="btn-reserve-header"
                        className="hidden lg:inline-flex btn-primary !py-2.5 !px-5 text-xs"
                    >
                        {t?.nav?.reserve || "Reservar"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="lg:hidden"
                        data-testid="mobile-menu-btn"
                        aria-label="Open menu"
                    >
                        <Menu size={28} color={scrolled ? "#233A5A" : "#fff"} />
                    </button>
                </div>
            </div>
        </header>

        {/* Mobile drawer - rendered as sibling (not inside header) so backdrop-blur doesn't trap `fixed` positioning */}
        {open && (
            <div
                className="fixed inset-0 z-[100]"
                style={{ backgroundColor: "#F9F8F6" }}
                data-testid="mobile-drawer"
            >
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <span className="font-display text-3xl text-brand-navy">Zarautz</span>
                    <button onClick={() => setOpen(false)} data-testid="mobile-close-btn" aria-label="Close">
                        <X size={28} color="#233A5A" />
                    </button>
                </div>
                <nav className="flex flex-col gap-4 px-6 pt-6">
                    {navItems(t).map((item) => (
                        <button
                            key={item.id}
                            onClick={() => go(item.id)}
                            className="text-left font-display text-4xl text-brand-navy py-3 border-b border-brand-navy/10"
                            data-testid={`mobile-nav-${item.id}`}
                        >
                            {item.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={openReserve}
                        className="btn-primary mt-8 justify-center"
                        data-testid="mobile-btn-reserve"
                    >
                        {t?.nav?.reserve || "Reservar"}
                    </button>
                </nav>
            </div>
        )}
        </>
    );
}
